import { useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { Play, Pause, RotateCcw, Rotate3d } from "lucide-react";
import { useLang } from "@/i18n";
import ThermalScene from "./ThermalScene";
import { thermalContent, PARTS } from "./content";
import { initialSimulation } from "./simulation";
import { partIcons } from "./Hotspot";
import { ThermalControls, ThermalReadouts } from "./ThermalControls";

export default function ThermalExplorer() {
  const { lang } = useLang();
  const copy = thermalContent[lang];
  const reducedMotion = useReducedMotion();
  const [running, setRunning] = useState(!reducedMotion);
  const [solar, setSolar] = useState(75);
  const [demand, setDemand] = useState(false);
  const [selected, setSelected] = useState("receiver");
  const [cameraRevision, setCameraRevision] = useState(0);
  const sim = useRef(initialSimulation());
  const [snapshot, setSnapshot] = useState(sim.current);
  const root = useRef();
  const visible = useInView(root, { margin: "200px" });
  const reset = () => {
    sim.current = initialSimulation();
    setSnapshot({ ...sim.current }); setSolar(75); setDemand(false); setRunning(!reducedMotion);
    setSelected("receiver"); setCameraRevision(n => n + 1);
  };
  const controlClass = "h-10 px-3 inline-flex items-center justify-center gap-2 border border-white/20 rounded-md text-sm hover:border-volt hover:text-volt transition-[color,border-color] focus-visible:outline focus-visible:outline-2 focus-visible:outline-volt";

  return (
    <div ref={root} className="mt-20 border-t border-white/10 pt-10" data-testid="thermal-explorer">
      <div className="flex flex-wrap justify-between items-center gap-6">
        <div>
          <p className="text-xs uppercase text-volt" data-testid="thermal-subtitle">{copy.subtitle}</p>
          <h3 className="mt-2 text-xl sm:text-2xl font-display font-semibold" data-testid="thermal-title">{copy.title}</h3>
        </div>
        <div className="flex gap-2" data-testid="thermal-toolbar">
          <button type="button" className={controlClass} onClick={() => setRunning(value => !value)} title={running ? copy.pause : copy.play} data-testid="thermal-play-pause">
            {running ? <Pause size={16} /> : <Play size={16} />} {running ? copy.pause : copy.play}
          </button>
          <button type="button" className={controlClass} onClick={() => setCameraRevision(n => n + 1)} title={copy.camera} aria-label={copy.camera} data-testid="thermal-reset-camera"><Rotate3d size={18} /></button>
          <button type="button" className={controlClass} onClick={reset} title={copy.reset} aria-label={copy.reset} data-testid="thermal-reset"><RotateCcw size={18} /></button>
        </div>
      </div>
      <p className="mt-4 text-xs text-steel leading-relaxed" data-testid="thermal-demo-disclaimer">{copy.demo}</p>
      <div className="relative mt-4 h-[340px] sm:h-[440px] lg:h-[520px] cursor-grab active:cursor-grabbing" data-testid="thermal-scene">
        <ThermalScene {...{ sim, solar, demand, running, selected, copy, visible, cameraRevision }} onSelect={setSelected} onTick={setSnapshot} />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 pb-5 text-xs text-steel" data-testid="fluid-legend">
        <span className="inline-flex items-center gap-2" data-testid="hot-fluid-legend"><span className="h-0.5 w-6 bg-red-400" />{copy.hot}</span>
        <span className="inline-flex items-center gap-2" data-testid="cold-fluid-legend"><span className="h-0.5 w-6 bg-blue-400" />{copy.cold}</span>
      </div>
      <ThermalReadouts {...{ snapshot, running, copy }} />
      <div className="flex flex-wrap gap-2 pt-6" aria-label={copy.title} data-testid="thermal-part-selector">
        {PARTS.map(part => {
          const Icon = partIcons[part];
          return <button key={part} type="button" onClick={() => setSelected(part)} aria-pressed={selected === part}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-[color,border-color] ${selected === part ? "text-volt border-volt" : "text-steel border-transparent hover:text-white hover:border-white/30"}`}
            data-testid={`select-${part}`}><Icon size={16} />{copy.parts[part].name}</button>;
        })}
      </div>
      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-8 lg:gap-16 mt-6">
        <div className="min-h-[150px]" aria-live="polite" data-testid="component-detail">
          <h4 className="text-lg font-display font-semibold" data-testid="component-detail-title">{copy.parts[selected].title}</h4>
          <p className="mt-3 text-sm text-steel leading-relaxed max-w-2xl" data-testid="component-detail-description">{copy.parts[selected].text}</p>
        </div>
        <ThermalControls {...{ solar, setSolar, demand, setDemand, copy }} />
      </div>
    </div>
  );
}